import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatStageLabel, formatCurrency, IMPLEMENTED_STAGES } from '../../utils/formatters';
import { db } from '../../config/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { writeDealAuditLog } from '../../services/auditService';
import SLABadge from './SLABadge';
import Badge from '../shared/UI/Badge';
import Button from '../shared/UI/Button';

// Tabs whose own predicate pins deal.status, and the status each one pins. The
// status filter is skipped on these (it would double-filter, and the default
// 'active' selection excludes every deal the tab counts), and the status select
// shows the pinned value, disabled. Keep in sync with the tab predicates.
const TAB_PINNED_STATUS = {
  suspended: 'suspended',
  denied: 'denied',
};

// DISPLAY-ONLY pipeline conversion of a deal's requested_amount to an arbitrary
// target currency, using the frozen rate map stamped on the deal at submission
// (NO live fetch). Mirrors convertMonetaryValue's base-match guard (Stage C) but
// adds a two-hop path so a deal whose requested_currency differs from its
// reporting/base currency still converts correctly. Never throws; any
// missing/zero rate → { ok:false } so the caller excludes + flags the deal
// rather than summing it at a wrong value. Does NOT touch scoring/normalisation.
//
//   rc     = requested_currency (the currency requested_amount is in)
//   base   = map.base (=== reporting_currency by construction)
//   rates  = map.rates, keyed base -> currency (1 base = rates[X] units of X)
//
// Returns { ok:true, value } or { ok:false, reason }.
export function convertRequested(deal, targetCurrency) {
  const amount = typeof deal?.requested_amount === 'number'
    ? deal.requested_amount
    : parseFloat(deal?.requested_amount);
  const rc = deal?.requested_currency;
  if (!targetCurrency || !rc || !Number.isFinite(amount)) {
    return { ok: false, reason: 'missing amount or currency' };
  }
  // (a) Already in the target currency — identity, no rates needed.
  if (rc === targetCurrency) return { ok: true, value: amount };

  const map = deal?.exchange_rates_at_submission;
  // (b) No usable rate map, or this submission's rate fetch failed.
  if (deal?.exchange_rate_fetch_failed || !map || !map.rates) {
    return { ok: false, reason: 'no exchange rates on deal' };
  }
  const base = map.base;
  const rates = map.rates;
  // (c) Drift guard: the stamped base must be the reporting currency.
  if (base && base !== deal?.reporting_currency) {
    return { ok: false, reason: `rate base ${base} != reporting currency ${deal?.reporting_currency}` };
  }
  const rateTo = rates[targetCurrency];
  if (!(rateTo > 0)) return { ok: false, reason: `no rate for ${targetCurrency}` };

  // (d) One-hop: requested currency IS the base → base -> target.
  if (rc === base) return { ok: true, value: amount * rateTo };

  // (e) Two-hop: requested currency -> base (divide by the FROM-rate) -> target
  //     (multiply by the TO-rate). Guard the divisor BEFORE dividing.
  const rateFrom = rates[rc];
  if (!(rateFrom > 0)) return { ok: false, reason: `no rate for ${rc}` };
  return { ok: true, value: (amount / rateFrom) * rateTo };
}

// Compact number for band-boundary labels (thousands-separated, no decimals).
function bandBound(v) {
  return Math.round(v).toLocaleString('en-US');
}

// Filter dropdown lists only the reachable stages (Item #2) — a filter option
// that can never match any deal is confusing. The table still reads/displays
// current_stage exactly as before.
const STAGE_LABELS_FILTER = IMPLEMENTED_STAGES.map(s => ({ value: s, label: formatStageLabel(s) }));

export default function DealTable({
  deals,
  slaMap,
  loading,
  currentUserId,
  currentUserRoles = [],
  currentUserDisplayName = '',
  initialTab = 'all',
  initialStatus = 'active',
  initialSlaFilter = '',
  initialStageFilter = '',
  activeFundId = null,
  activeStreamId = null,
  activeFund = null,
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [reopenLoading, setReopenLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFund] = useState('');
  const [filterStage, setFilterStage] = useState(initialStageFilter);
  const [filterStatus, setFilterStatus] = useState(initialStatus);
  const [filterAnalyst] = useState('');
  const [filterSla, setFilterSla] = useState(initialSlaFilter); // 'breached' or ''
  const [filterPrescreen, setFilterPrescreen] = useState(''); // '', 'advance', 'decline'
  const [filterSector, setFilterSector] = useState(''); // '' or a sector string
  const [filterGender, setFilterGender] = useState(''); // '', 'women_led', 'not_women_led'
  const [filterCountry, setFilterCountry] = useState(''); // '' or a country string
  const [filterTicket, setFilterTicket] = useState(''); // '', 'under', 'low', 'mid', 'over'

  // Single-fund vs All Funds is decided by activeFundId (per useActiveFund),
  // NOT by inspecting the loaded deals. The PIF-score column and ticket-band
  // filter are single-fund-only; the total's target currency depends on it too.
  const isSingleFund = !!activeFundId;
  const fundBase = activeFund?.base_currency || null;
  const avgTicket =
    typeof activeFund?.average_ticket_size === 'number' &&
    activeFund.average_ticket_size > 0
      ? activeFund.average_ticket_size
      : null;
  // Ticket bands need both a single fund AND a numeric average_ticket_size.
  const showTicketFilter = isSingleFund && avgTicket != null && !!fundBase;
  // Total-capital target: the selected fund's base currency, else USD (All Funds).
  const totalTarget = isSingleFund ? (fundBase || 'USD') : 'USD';

  const canReopenDeals = currentUserRoles.includes('admin') || currentUserRoles.includes('head_of_pipeline');

  async function handleReopenDeal(deal, e) {
    e.stopPropagation();
    if (!window.confirm(`Reopen this deal? "${deal.company_name}" will return to Analyst Review stage for reassessment.`)) return;
    setReopenLoading(deal.id);
    try {
      await updateDoc(doc(db, 'deals', deal.id), {
        status: 'active',
        current_stage: 'analyst_review',
        reopened_at: new Date().toISOString(),
        reopened_by: currentUserId,
        reopened_by_name: currentUserDisplayName,
        suspension_reason: null,
        suspended_at: null,
      });
      await writeDealAuditLog({
        dealId: deal.id,
        action: 'deal_reopened',
        performedBy: currentUserId,
        performedByName: currentUserDisplayName,
        details: { previous_status: 'suspended', returned_to_stage: 'analyst_review' },
        stageAtTime: deal.current_stage,
      });
    } catch (err) {
      alert(err.message);
    } finally {
      setReopenLoading(null);
    }
  }

  const unassignedCount = deals.filter(d => !d.assigned_analyst_id && d.status === 'active').length;
  const myDealsCount = deals.filter(d => d.assigned_analyst_id === currentUserId).length;
  const pendingActionCount = deals.filter(d => {
    if (d.assigned_analyst_id === currentUserId && d.current_stage === 'analyst_review') return true;
    if (d.peer_reviewer_id === currentUserId && d.current_stage === 'peer_review') return true;
    if (currentUserRoles.includes('head_of_pipeline') && d.current_stage === 'hop_review') return true;
    if (['head_of_pipeline', 'cio', 'md'].some(r => currentUserRoles.includes(r)) && d.current_stage === 'iic_approval') return true;
    return false;
  }).length;
  const slaBreachedCount = Object.values(slaMap || {}).filter(s => s.status === 'red').length;

  // Dropdown sources derived from the loaded deals — only values actually
  // present appear as options.
  const sectorOptions = Array.from(new Set(deals.map(d => d.sector).filter(Boolean))).sort();
  const countryOptions = Array.from(new Set(deals.map(d => d.company_country).filter(Boolean))).sort();

  // Tabs that pin deal status themselves, via their own predicate below. On these
  // the status filter is redundant, and the default 'active' selection would
  // contradict the tab outright — filtering out every deal the tab counts.
  const tabPinnedStatus = TAB_PINNED_STATUS[tab] || null;

  const filtered = deals.filter(d => {
    if (searchTerm && !d.company_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterFund && d.fund_id !== filterFund) return false;
    // Stream scope, from the FundSwitcher's stream selector. "All streams"
    // (activeStreamId null/'') matches everything. Forward-only: deals submitted
    // before per-stream links existed carry stream_id null and so appear only
    // under All streams.
    if (activeStreamId && d.stream_id !== activeStreamId) return false;
    if (filterStage && d.current_stage !== filterStage) return false;
    // Under the 'active' selection, treat 'information_pending' as active-equivalent
    // so IIC deals flagged Info Pending stay visible in the default view. Any other
    // status filter (suspended/denied/information_pending) still matches exactly.
    // Skipped on tabs that pin a status themselves (see TAB_PINNED_STATUS):
    // applying it there would double-filter, and the default 'active' selection
    // would reject the very deals those tabs count.
    if (!tabPinnedStatus && filterStatus && filterStatus !== 'all') {
      if (filterStatus === 'active') {
        if (d.status !== 'active' && d.status !== 'information_pending') return false;
      } else if (d.status !== filterStatus) return false;
    }
    if (filterAnalyst === 'unassigned' && d.assigned_analyst_id) return false;
    if (filterAnalyst && filterAnalyst !== 'unassigned' && d.assigned_analyst_id !== filterAnalyst) return false;
    if (filterSla === 'breached' && slaMap?.[d.id]?.status !== 'red') return false;
    // Pre-screening result (threshold). Missing/null matches neither option.
    if (filterPrescreen === 'advance' && d.pif_score_meets_threshold !== true) return false;
    if (filterPrescreen === 'decline' && d.pif_score_meets_threshold !== false) return false;
    // Sector
    if (filterSector && d.sector !== filterSector) return false;
    // Gender leadership. null matches neither option.
    if (filterGender === 'women_led' && d.is_women_led !== true) return false;
    if (filterGender === 'not_women_led' && d.is_women_led !== false) return false;
    // Location (country)
    if (filterCountry && d.company_country !== filterCountry) return false;
    // Ticket-size band (single-fund + has average only). Compares the requested
    // amount CONVERTED TO FUND BASE; a deal that can't be converted matches no
    // band (excluded), consistent with the total's exclude-and-flag guard.
    if (filterTicket && showTicketFilter) {
      const r = convertRequested(d, fundBase);
      if (!r.ok) return false;
      const v = r.value;
      if (filterTicket === 'under' && !(v < 0.75 * avgTicket)) return false;
      if (filterTicket === 'low' && !(v >= 0.75 * avgTicket && v < avgTicket)) return false;
      if (filterTicket === 'mid' && !(v >= avgTicket && v <= 1.25 * avgTicket)) return false;
      if (filterTicket === 'over' && !(v > 1.25 * avgTicket)) return false;
    }
    if (tab === 'unassigned') return !d.assigned_analyst_id && d.status === 'active';
    if (tab === 'mine') return d.assigned_analyst_id === currentUserId;
    if (tab === 'pending-action') {
      if (d.assigned_analyst_id === currentUserId && d.current_stage === 'analyst_review') return true;
      if (d.peer_reviewer_id === currentUserId && d.current_stage === 'peer_review') return true;
      if (currentUserRoles.includes('head_of_pipeline') && d.current_stage === 'hop_review') return true;
      if (['head_of_pipeline', 'cio', 'md'].some(r => currentUserRoles.includes(r)) && d.current_stage === 'iic_approval') return true;
      return false;
    }
    if (tab === 'suspended') return d.status === 'suspended';
    if (tab === 'denied') return d.status === 'denied';
    return true;
  });

  // Total capital requested over the FILTERED rows (updates live with every
  // filter). Convertible deals are summed in totalTarget; unconvertible deals
  // are counted as excluded and NEVER summed at a wrong value.
  let totalCapital = 0;
  let excludedCapital = 0;
  filtered.forEach(d => {
    const r = convertRequested(d, totalTarget);
    if (r.ok) totalCapital += r.value;
    else excludedCapital += 1;
  });

  const tabs = [
    { id: 'all', label: 'All Deals', count: deals.filter(d => d.status !== 'denied').length },
    { id: 'pending-action', label: 'Pending My Action', count: pendingActionCount, alert: pendingActionCount > 0 },
    { id: 'unassigned', label: 'Unassigned', count: unassignedCount, alert: unassignedCount > 0 },
    { id: 'mine', label: 'My Deals', count: myDealsCount },
    { id: 'suspended', label: 'Suspended', count: deals.filter(d => d.status === 'suspended').length },
    { id: 'denied', label: 'Denied', count: deals.filter(d => d.status === 'denied').length },
  ];

  if (loading) {
    return (
      <div className="card">
        <div className="spinner-wrap"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="card">
      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', padding: '0 16px' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '12px 16px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              borderBottom: tab === t.id ? '2px solid var(--color-accent)' : '2px solid transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
            }}
          >
            {t.label}
            {t.count > 0 && (
              <span style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '1px 6px',
                borderRadius: 20,
                background: t.alert ? 'var(--color-error)' : 'var(--color-border)',
                color: t.alert ? '#fff' : 'var(--color-text-secondary)',
              }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Unassigned SLA warning banner */}
      {tab === 'unassigned' && unassignedCount > 0 && (
        <div className="alert alert-warning" style={{ margin: '12px 16px 0', borderRadius: 'var(--radius)' }}>
          {unassignedCount} deal{unassignedCount !== 1 ? 's' : ''} awaiting assignment.
          Review and assign as soon as possible.
        </div>
      )}

      {/* Filters */}
      <div style={{ padding: '12px 16px', display: 'flex', gap: 10, flexWrap: 'wrap', borderBottom: '1px solid var(--color-border)' }}>
        <input
          className="form-input"
          style={{ width: 200 }}
          placeholder="Search by company name…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <select
          className="form-select"
          style={{ width: 140 }}
          value={tabPinnedStatus || filterStatus}
          disabled={!!tabPinnedStatus}
          title={tabPinnedStatus ? `This tab always shows ${tabPinnedStatus} deals.` : undefined}
          onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="information_pending">Info Pending</option>
          <option value="suspended">Suspended</option>
          <option value="denied">Denied</option>
        </select>
        <select className="form-select" style={{ width: 160 }} value={filterStage} onChange={e => setFilterStage(e.target.value)}>
          <option value="">All stages</option>
          {STAGE_LABELS_FILTER.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select
          className="form-select"
          style={{ width: 150, borderColor: filterSla === 'breached' ? 'var(--color-error)' : undefined }}
          value={filterSla}
          onChange={e => setFilterSla(e.target.value)}
        >
          <option value="">All SLA status</option>
          <option value="breached">SLA Breached ({slaBreachedCount})</option>
        </select>
        <select className="form-select" style={{ width: 170 }} value={filterPrescreen} onChange={e => setFilterPrescreen(e.target.value)}>
          <option value="">All pre-screening</option>
          <option value="advance">Advance</option>
          <option value="decline">Don't Advance</option>
        </select>
        <select className="form-select" style={{ width: 180 }} value={filterSector} onChange={e => setFilterSector(e.target.value)}>
          <option value="">All sectors</option>
          {sectorOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="form-select" style={{ width: 170 }} value={filterGender} onChange={e => setFilterGender(e.target.value)}>
          <option value="">All gender leadership</option>
          <option value="women_led">Women-led</option>
          <option value="not_women_led">Not women-led</option>
        </select>
        <select className="form-select" style={{ width: 160 }} value={filterCountry} onChange={e => setFilterCountry(e.target.value)}>
          <option value="">All locations</option>
          {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {showTicketFilter && (
          <select className="form-select" style={{ width: 230 }} value={filterTicket} onChange={e => setFilterTicket(e.target.value)}>
            <option value="">All ticket sizes</option>
            <option value="under">Under −25% (&lt; {bandBound(0.75 * avgTicket)})</option>
            <option value="low">−25% to avg ({bandBound(0.75 * avgTicket)}–{bandBound(avgTicket)})</option>
            <option value="mid">avg to +25% ({bandBound(avgTicket)}–{bandBound(1.25 * avgTicket)})</option>
            <option value="over">Over +25% (&gt; {bandBound(1.25 * avgTicket)})</option>
          </select>
        )}
      </div>

      {/* Total capital requested — derived from `filtered`, updates live. */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--color-border)', fontSize: 14 }}>
        <span style={{ color: 'var(--color-text-secondary)' }}>Total capital requested: </span>
        <span style={{ fontWeight: 700, color: 'var(--color-text)' }}>{formatCurrency(totalCapital, totalTarget)}</span>
        {/* N = all filtered rows (operator default). To count only convertible
            deals instead, base this on the ok-conversion subset
            (filtered.length - excludedCapital). */}
        <span style={{ color: 'var(--color-text-secondary)' }}>
          {' '}across {filtered.length} deal{filtered.length !== 1 ? 's' : ''}
        </span>
        {excludedCapital > 0 && (
          <span style={{ color: 'var(--color-warning)', marginLeft: 8 }}>
            — {excludedCapital} deal{excludedCapital !== 1 ? 's' : ''} excluded (missing conversion)
          </span>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h3>No deals found</h3>
          <p>No deals match the current filters.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Company</th>
                {isSingleFund && <th>PIF Score</th>}
                <th>Sector</th>
                <th>Amount</th>
                <th>Stage</th>
                <th>Status</th>
                <th>Analyst</th>
                <th>SLA</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(deal => {
                const sla = slaMap?.[deal.id];
                const analystName = deal.assigned_analyst_name || deal.assigned_analyst_id || '—';
                return (
                  <tr key={deal.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/pipeline/${deal.id}`)}>
                    <td>
                      <p style={{ fontWeight: 600, fontSize: 14 }}>{deal.company_name}</p>
                      <p style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{deal.company_country}</p>
                    </td>
                    {isSingleFund && (
                      <td style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap',
                        color: deal.pif_score == null
                          ? 'var(--color-text-secondary)'
                          : deal.pif_score >= 7
                            ? 'var(--color-success)'
                            : deal.pif_score >= 4
                              ? 'var(--color-warning)'
                              : 'var(--color-error)',
                      }}>
                        {deal.pif_score != null ? Number(deal.pif_score).toFixed(1) : '—'}
                      </td>
                    )}
                    <td style={{ fontSize: 13 }}>{deal.sector || '—'}</td>
                    <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                      {formatCurrency(deal.requested_amount, deal.requested_currency)}
                    </td>
                    <td>
                      <span style={{
                        fontSize: 12,
                        fontWeight: 500,
                        padding: '2px 8px',
                        borderRadius: 4,
                        background: 'var(--color-border-light)',
                      }}>
                        {formatStageLabel(deal.current_stage)}
                      </span>
                      {deal.status === 'information_pending' && (
                        <span style={{
                          marginLeft: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '2px 6px',
                          borderRadius: 4,
                          background: 'var(--color-warning-bg)',
                          color: 'var(--color-warning)',
                        }}>
                          Info Pending
                        </span>
                      )}
                      {deal.current_stage === 'screening' && (
                        <span style={{
                          marginLeft: 6,
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '2px 6px',
                          borderRadius: 4,
                          background: deal.dsq_company_submitted
                            ? 'var(--color-success-bg)'
                            : deal.dsq_initiated
                              ? 'var(--color-warning-bg)'
                              : 'var(--color-border)',
                          color: deal.dsq_company_submitted
                            ? 'var(--color-success)'
                            : deal.dsq_initiated
                              ? 'var(--color-warning)'
                              : 'var(--color-text-secondary)',
                        }}>
                          {deal.dsq_company_submitted
                            ? 'DSQ Submitted'
                            : deal.dsq_initiated
                              ? 'DSQ Pending'
                              : 'DSQ Not Started'}
                        </span>
                      )}
                    </td>
                    <td>
                      <Badge
                        text={deal.status === 'information_pending' ? 'Info Pending' : (deal.status || 'active')}
                        variant={deal.status === 'information_pending' ? 'warning' : undefined}
                      />
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {deal.assigned_analyst_id ? analystName : (
                        <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>Unassigned</span>
                      )}
                    </td>
                    <td>{sla ? <SLABadge {...sla} /> : '—'}</td>
                    <td onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <Button size="sm" variant="secondary" onClick={() => navigate(`/pipeline/${deal.id}`)}>
                        View
                      </Button>
                      {tab === 'suspended' && canReopenDeals && deal.status === 'suspended' && (
                        <Button
                          size="sm"
                          variant="primary"
                          loading={reopenLoading === deal.id}
                          onClick={e => handleReopenDeal(deal, e)}
                        >
                          Reopen
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
